import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  ACADEMY_CHAPTERS,
  ACADEMY_I18N_PREFIX,
  ACADEMY_QUIZ_OPTIONS,
  ACADEMY_SLIDES,
  ACADEMY_START_STEPS,
} from "./RentmatchAcademy.constants";
import {
  EAcademyBlockType,
  EAcademyChapter,
  IAcademyChapter,
  IAcademyScore,
  TAcademyBlock,
  TAcademyQuizAnswers,
} from "./RentmatchAcademy.types";

const BULLET_PREFIX = "- ";

// The academy copy exists in Dutch only for now (DEV-3526), and getLocalizedText
// has no fallback between languages: a key without a French value renders as
// the raw key for a French-speaking broker. Every academy text is therefore
// read in Dutch, whatever the dashboard language. Once French and English are
// added in Lokalise, dropping the locale here is the whole change.
export const getAcademyText = (
  key: string,
  values: { [key: string]: string } = {},
) => getLocalizedText(key, values, 0, ELocale.NL);

export const getChapterKey = (chapterId: EAcademyChapter, field: string) =>
  `${ACADEMY_I18N_PREFIX}.chapter.${chapterId}.${field}`;

export const getSectionKey = (
  chapterId: EAcademyChapter,
  sectionId: string,
  field: string,
) =>
  `${ACADEMY_I18N_PREFIX}.chapter.${chapterId}.section.${sectionId}.${field}`;

export const getQuizKey = (
  chapterId: EAcademyChapter,
  sectionId: string,
  field: string,
) => getSectionKey(chapterId, sectionId, `quiz.${field}`);

export const getSlideKey = (slideId: string, field: string) =>
  `${ACADEMY_I18N_PREFIX}.slide.${slideId}.${field}`;

export const getUiKey = (suffix: string) => `${ACADEMY_I18N_PREFIX}.${suffix}`;

export const getStartStepKey = (stepId: string, field: string) =>
  getUiKey(`start.step.${stepId}.${field}`);

// The element a search result scrolls to: a section's card, or a question in
// the FAQ.
export const getSectionAnchor = (
  chapterId: string | undefined,
  sectionId: string,
) => `academy-${chapterId}-${sectionId}`;

export const getQuizAnswerKey = (
  chapterId: EAcademyChapter,
  sectionId: string,
) => `${chapterId}.${sectionId}`;

// Right answers against the questions answered so far, and how far along the
// chapter the broker is.
export const getChapterScore = (
  chapter: IAcademyChapter,
  answers: TAcademyQuizAnswers,
): IAcademyScore => {
  const questions = chapter.sections.filter(section => section.quizAnswer);
  const picks = questions.map(section => ({
    pick: answers[getQuizAnswerKey(chapter.id, section.id)],
    answer: section.quizAnswer,
  }));

  return {
    total: questions.length,
    answered: picks.filter(({ pick }) => pick !== undefined).length,
    correct: picks.filter(({ pick, answer }) => pick === answer).length,
  };
};

export const getAcademyScore = (answers: TAcademyQuizAnswers): IAcademyScore =>
  ACADEMY_CHAPTERS.map(chapter => getChapterScore(chapter, answers)).reduce(
    (sum, score) => ({
      correct: sum.correct + score.correct,
      answered: sum.answered + score.answered,
      total: sum.total + score.total,
    }),
    { correct: 0, answered: 0, total: 0 },
  );

export const getChapterById = (chapterId?: string) =>
  ACADEMY_CHAPTERS.find(chapter => chapter.id === chapterId);

export const getChapterNumber = (chapter: IAcademyChapter) =>
  ACADEMY_CHAPTERS.indexOf(chapter) + 1;

// Every key the academy builds at runtime. `yarn lint:i18n` only sees literal
// keys, so the academy test resolves these instead.
export const getAllAcademyKeys = () => [
  ...ACADEMY_CHAPTERS.flatMap(chapter => [
    getChapterKey(chapter.id, "title"),
    getChapterKey(chapter.id, "intro"),
    ...chapter.sections.flatMap(section => [
      getSectionKey(chapter.id, section.id, "title"),
      getSectionKey(chapter.id, section.id, "body"),
      ...(section.quizAnswer
        ? [
            getQuizKey(chapter.id, section.id, "question"),
            getQuizKey(chapter.id, section.id, "explanation"),
            ...ACADEMY_QUIZ_OPTIONS.map(option =>
              getQuizKey(chapter.id, section.id, `option.${option}`),
            ),
          ]
        : []),
    ]),
  ]),
  ...ACADEMY_START_STEPS.flatMap(step => [
    getStartStepKey(step.id, "title"),
    getStartStepKey(step.id, "body"),
    ...(step.links ?? []).map(link =>
      getStartStepKey(step.id, `link.${link.id}`),
    ),
  ]),
  ...ACADEMY_SLIDES.flatMap(slide => [
    getSlideKey(slide.id, "label"),
    getSlideKey(slide.id, "title"),
    getSlideKey(slide.id, "body"),
  ]),
];

// Body copy is plain text: paragraphs are separated by a blank line, and lines
// starting with "- " are bullets. A paragraph may mix both, such as an intro
// line followed by its list, and keeps them in their order.
export const parseAcademyBody = (text: string): TAcademyBlock[] =>
  text.split(/\n\s*\n/).flatMap(paragraph => {
    const blocks: TAcademyBlock[] = [];
    paragraph
      .split("\n")
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .forEach(line => {
        const last = blocks[blocks.length - 1];
        if (line.startsWith(BULLET_PREFIX)) {
          const item = line.slice(BULLET_PREFIX.length);
          if (last?.type === EAcademyBlockType.Bullets) {
            last.items.push(item);
          } else {
            blocks.push({ type: EAcademyBlockType.Bullets, items: [item] });
          }

          return;
        }
        if (last?.type === EAcademyBlockType.Paragraph) {
          last.text = `${last.text} ${line}`;
        } else {
          blocks.push({ type: EAcademyBlockType.Paragraph, text: line });
        }
      });

    return blocks;
  });
